"use client";

import * as React from "react";

import { cn } from "./utils";

/**
 * Sticky 컬럼 지원을 위한 공통 타입
 *
 * stickyLeft / stickyRight를 제공하면 자동으로:
 * - position: sticky + left/right 위치 설정
 * - z-index 설정 (TableHead: z-30, TableCell: z-10)
 * - 불투명 배경 (TableHead: bg-muted, TableCell: bg-card)
 * - box-shadow 경계선 (stickyLeft→우측, stickyRight→좌측 + depth shadow)
 *
 * TableHeader gradient prop:
 * - gradient={true}로 설정하면 --primary-gradient 배경 적용
 * - 하위 TableHead 셀은 자동으로 투명 배경 + 흰색 텍스트로 전환
 *
 * 참고: border-separate + border-spacing-0 조합 사용
 * - border-collapse는 sticky 요소와 충돌하여 스크롤 시 border가 소실됨
 * - border-separate + spacing-0은 시각적으로 동일하면서 sticky 호환
 * @see https://github.com/TanStack/table/discussions/4471
 */
interface StickyProps {
  /** sticky left 위치값 (예: "0", "60px", "220px") */
  stickyLeft?: string;
  /** sticky right 위치값 (예: "0", "60px") */
  stickyRight?: string;
}

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm border-separate border-spacing-0 border-l border-t border-border", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, gradient, ...props }: React.ComponentProps<"thead"> & { gradient?: boolean }) {
  return (
    <thead
      data-slot="table-header"
      data-gradient={gradient || undefined}
      className={cn(
        "group/thead sticky top-0 z-20",
        gradient ? "" : "bg-muted",
        className,
      )}
      style={gradient ? { background: "var(--primary-gradient)" } : undefined}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("bg-card", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted border-t border-border font-medium",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, stickyLeft, stickyRight, style, ...props }: React.ComponentProps<"th"> & StickyProps) {
  const posStyle = {
    ...(stickyLeft != null ? { left: stickyLeft } : {}),
    ...(stickyRight != null ? { right: stickyRight } : {}),
    ...style,
  };

  return (
    <th
      data-slot="table-head"
      className={cn(
        "px-3 py-2 text-center text-xs font-semibold whitespace-nowrap border-r border-b [&:has([role=checkbox])]:px-2 [&>[role=checkbox]]:translate-y-[2px]",
        // default: muted 배경
        "bg-muted text-muted-foreground border-border",
        // gradient: 투명 배경 + 흰색 텍스트
        "group-data-[gradient]/thead:bg-transparent group-data-[gradient]/thead:text-primary-foreground group-data-[gradient]/thead:border-white/20",
        stickyLeft != null && "sticky z-30 shadow-[2px_0_0_0_var(--border)] group-data-[gradient]/thead:shadow-[2px_0_0_0_rgba(255,255,255,0.2)]",
        stickyRight != null && "sticky z-30 shadow-[-2px_0_0_0_var(--border),-10px_0_8px_-8px_rgba(5,5,5,0.06)] group-data-[gradient]/thead:shadow-[-2px_0_0_0_rgba(255,255,255,0.2)]",
        className,
      )}
      style={posStyle}
      {...props}
    />
  );
}

function TableCell({ className, stickyLeft, stickyRight, style, ...props }: React.ComponentProps<"td"> & StickyProps) {
  const posStyle = {
    ...(stickyLeft != null ? { left: stickyLeft } : {}),
    ...(stickyRight != null ? { right: stickyRight } : {}),
    ...style,
  };

  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-3 py-1.5 text-sm text-foreground border-r border-b border-border whitespace-nowrap [&:has([role=checkbox])]:px-2 [&>[role=checkbox]]:translate-y-[2px]",
        stickyLeft != null && "sticky z-10 bg-card shadow-[2px_0_0_0_var(--border)]",
        stickyRight != null && "sticky z-10 bg-card shadow-[-2px_0_0_0_var(--border),-10px_0_8px_-8px_rgba(5,5,5,0.06)]",
        className,
      )}
      style={posStyle}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
